import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useAudioRecorder — wraps browser MediaRecorder for 30-second audio capture.
 * Returns { start, stop, isRecording, timeLeft, error }.
 */
export function useAudioRecorder({ onRecordingComplete, maxDurationMs = 30000 } = {}) {
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [timeLeft, setTimeLeft] = useState(maxDurationMs / 1000); // seconds
  const [error, setError] = useState(null);

  const stop = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        throw new Error(`Audio format ${mimeType} not supported by this browser.`);
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Stop all audio tracks to release microphone
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;

        // Build the Blob
        const audioBlob = new Blob(chunksRef.current, { type: mimeType });
        onRecordingComplete?.(audioBlob);
      };

      mediaRecorder.onerror = (e) => {
        setError(`MediaRecorder error: ${e.error}`);
        stop();
      };

      mediaRecorder.start();
      setIsRecording(true);
      setTimeLeft(maxDurationMs / 1000);

      // Countdown timer
      let elapsedMs = 0;
      timerRef.current = setInterval(() => {
        elapsedMs += 100;
        const remainingSec = Math.max(0, (maxDurationMs - elapsedMs) / 1000);
        setTimeLeft(remainingSec);

        if (elapsedMs >= maxDurationMs) {
          stop();
        }
      }, 100);
    } catch (err) {
      setError(err.message || 'Failed to access microphone.');
      setIsRecording(false);
      // Clean up stream if it was partially opened
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, [maxDurationMs, onRecordingComplete, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, [stop]);

  return { start, stop, isRecording, timeLeft, error };
}
